import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Loader2,
  LogOut,
  RefreshCcw,
  Phone,
  MessageCircle,
  Mail,
  Calendar,
  Users,
  MapPin,
  CheckCircle2,
  XCircle,
  Clock3,
  Trash2,
  ShieldCheck,
  FileSignature,
  Euro,
  Save,
  FileDown,
  Send,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/translations";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendente", icon: Clock3, color: "text-amber-400" },
  { value: "confirmed", label: "Confirmada", icon: CheckCircle2, color: "text-emerald-400" },
  { value: "completed", label: "Concluída", icon: CheckCircle2, color: "text-[color:var(--gold)]" },
  { value: "cancelled", label: "Cancelada", icon: XCircle, color: "text-red-400" },
];

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const Icon = opt.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase ${opt.color}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {opt.label}
    </span>
  );
};

const AdminDashboard = () => {
  const { user, setUser, loading: authLoading, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("reservas"); // 'reservas' | 'contratos'

  useEffect(() => {
    if (location.state?.user && !user) setUser(location.state.user);
  }, [location.state, user, setUser]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      checkAuth();
    }
  }, [authLoading, user, checkAuth]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (!user.is_admin) {
      setError("Esta conta não tem permissões de admin.");
      setLoading(false);
      return;
    }
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bookings");
      setBookings(res.data);
      setError(null);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err?.response?.data?.detail || "Falha ao carregar reservas.");
    } finally {
      setLoading(false);
    }
  };

  const updateBooking = async (id, patch) => {
    try {
      const res = await api.patch(`/admin/bookings/${id}`, patch);
      setBookings((bs) => bs.map((b) => (b.id === id ? res.data : b)));
      return res.data;
    } catch {
      toast.error("Erro ao atualizar");
      return null;
    }
  };

  const updateStatus = async (id, status) => {
    const r = await updateBooking(id, { status });
    if (r) toast.success("Estado atualizado");
  };

  const updateValue = async (id, patch) => {
    const r = await updateBooking(id, patch);
    if (r) toast.success("Contrato gravado");
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Eliminar este registo?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Registo eliminado");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const reservas = useMemo(
    () => bookings.filter((b) => b.value === null || b.value === undefined),
    [bookings]
  );
  const contratos = useMemo(
    () => bookings.filter((b) => b.value !== null && b.value !== undefined),
    [bookings]
  );

  const visible = tab === "reservas" ? reservas : contratos;

  if (authLoading || (loading && !error)) {
    return (
      <div className="min-h-screen bg-[color:var(--bg-primary)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[color:var(--gold)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--bg-primary)] text-white">
      <header className="border-b border-white/5 bg-[color:var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-logo">
            <img
              src={COMPANY.logo}
              alt="Faustitur"
              className="h-9 w-9 object-cover rounded-sm border border-white/10"
            />
            <div className="leading-tight">
              <p className="font-display text-base text-white">Faustitur</p>
              <p className="text-[9px] tracking-[0.3em] uppercase text-[color:var(--teal)]">
                Admin
              </p>
            </div>
          </a>
          <div className="flex items-center gap-4">
            {user?.email && (
              <span className="hidden sm:flex items-center gap-2 text-xs text-zinc-400">
                <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--gold)]" />
                {user.email}
              </span>
            )}
            <button
              data-testid="admin-refresh"
              onClick={fetchBookings}
              className="text-zinc-300 hover:text-[color:var(--gold)] p-2 transition-colors"
              aria-label="Refresh"
            >
              <RefreshCcw className="h-4 w-4" />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-xs text-zinc-300 hover:text-red-400 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className="label-eyebrow">Painel</p>
            <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">
              Reservas & Contratos
            </h1>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Total" value={bookings.length} testid="stat-total" />
            <Stat
              label="Reservas"
              value={reservas.length}
              testid="stat-reservas"
              highlight={tab === "reservas"}
            />
            <Stat
              label="Contratos"
              value={contratos.length}
              testid="stat-contratos"
              highlight={tab === "contratos"}
            />
          </div>
        </div>

        {/* Tabs */}
        <div
          data-testid="admin-tabs"
          className="mb-8 flex items-center gap-1 border border-white/10 bg-[color:var(--bg-surface)] p-1 w-fit"
        >
          <TabButton
            active={tab === "reservas"}
            onClick={() => setTab("reservas")}
            testid="tab-reservas"
            icon={Clock3}
            label="Reservas"
            count={reservas.length}
          />
          <TabButton
            active={tab === "contratos"}
            onClick={() => setTab("contratos")}
            testid="tab-contratos"
            icon={FileSignature}
            label="Contratos"
            count={contratos.length}
          />
        </div>

        {tab === "contratos" && (
          <p className="text-xs text-zinc-500 mb-4 max-w-3xl leading-relaxed">
            Contrato de transporte conforme <strong className="text-zinc-300">Artigo 18.º alínea c) da Lei n.º 101/2023, de 31 de outubro</strong> e{" "}
            <strong className="text-zinc-300">Artigo 10.º alínea b)</strong> do Regulamento de
            Transporte Público de Passageiros em Táxi do Município de Portimão (DR II série, 1/04/2025).
            O valor é acordado no ato e preenchido pela Faustitur.
          </p>
        )}

        {error && (
          <div
            data-testid="admin-error"
            className="border border-red-500/30 bg-red-500/10 text-red-300 px-5 py-4 rounded-sm mb-6"
          >
            {error}
          </div>
        )}

        {!error && visible.length === 0 && (
          <div
            data-testid="admin-empty"
            className="border border-white/10 bg-[color:var(--bg-surface)] px-6 py-16 text-center"
          >
            <p className="font-display text-2xl">
              {tab === "reservas" ? "Sem reservas por agora" : "Sem contratos por agora"}
            </p>
            <p className="text-zinc-500 text-sm mt-2">
              {tab === "reservas"
                ? "As novas reservas vão aparecer aqui automaticamente."
                : "Defina um valor numa reserva para a transformar em contrato."}
            </p>
          </div>
        )}

        <div className="grid gap-4">
          {visible.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              onStatus={updateStatus}
              onDelete={deleteBooking}
              onValue={updateValue}
              contractView={tab === "contratos"}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

const TabButton = ({ active, onClick, testid, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors ${
      active
        ? "bg-[color:var(--gold)] text-black"
        : "text-zinc-300 hover:text-[color:var(--gold)]"
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
    <span
      className={`ml-1 text-[10px] px-1.5 py-0.5 ${
        active ? "bg-black/20 text-black" : "bg-white/5 text-zinc-400"
      }`}
    >
      {count}
    </span>
  </button>
);

const Stat = ({ label, value, testid, highlight }) => (
  <div
    data-testid={testid}
    className={`border ${
      highlight ? "border-[color:var(--gold)]/40" : "border-white/10"
    } px-4 py-3 bg-[color:var(--bg-surface)]`}
  >
    <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</p>
    <p
      className={`font-display text-2xl mt-1 ${
        highlight ? "gold-text" : "text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

const BookingRow = ({ booking: b, onStatus, onDelete, onValue, contractView }) => {
  const [draftValue, setDraftValue] = useState(
    b.value !== null && b.value !== undefined ? String(b.value) : ""
  );
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftAddress, setDraftAddress] = useState(b.address || "");
  const [draftCountry, setDraftCountry] = useState(b.country || "");
  const [draftTaxId, setDraftTaxId] = useState(b.tax_id || "");

  let created = "";
  try {
    created = new Date(b.created_at).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    created = String(b.created_at);
  }

  const saveValue = async () => {
    const num = parseFloat(String(draftValue).replace(",", "."));
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Valor inválido");
      return;
    }
    await onValue(b.id, {
      value: num,
      address: draftAddress,
      country: draftCountry,
      tax_id: draftTaxId,
    });
  };

  const openPdf = () => {
    window.open(`${API}/admin/bookings/${b.id}/contract.pdf`, "_blank", "noopener");
  };

  const sendEmail = async () => {
    if (!b.email) {
      toast.error("Este cliente não tem email registado");
      return;
    }
    if (b.value === null || b.value === undefined) {
      toast.error("Defina primeiro o valor do contrato");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/bookings/${b.id}/send-contract`);
      if (res.data?.fallback) {
        toast.success(
          `Contrato enviado para ${res.data.sent_to} (reencaminhe ao cliente)`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Contrato enviado para ${res.data?.sent_to || b.email}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar email");
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = () => {
    const digits = (b.phone || "").replace(/[^0-9]/g, "");
    if (!digits) {
      toast.error("Sem número de telefone");
      return;
    }
    const value = b.value ? `${Number(b.value).toFixed(2).replace(".", ",")} €` : "a acordar";
    const msg =
      `Olá ${b.name || ""},%0A%0A` +
      `Segue o contrato de transporte Faustitur — Tarifa C:%0A` +
      `• Origem: ${b.pickup}%0A` +
      `• Destino: ${b.destination}%0A` +
      `• Data: ${b.date} · ${b.time}%0A` +
      `• Passageiros: ${b.passengers}%0A` +
      `• Valor acordado: ${value}%0A%0A` +
      `Anexo o PDF do contrato em seguida.%0AObrigado — Faustitur.`;
    window.open(`https://wa.me/${digits}?text=${msg}`, "_blank", "noopener");
  };

  const hasContract = b.value !== null && b.value !== undefined;

  return (
    <article
      data-testid={`booking-row-${b.id}`}
      className={`border ${
        hasContract
          ? "border-[color:var(--gold)]/40 bg-[color:var(--bg-surface)]"
          : "border-white/10 bg-[color:var(--bg-surface)]"
      } hover:border-[color:var(--gold)]/60 transition-colors`}
    >
      <div className="p-5 sm:p-6 grid lg:grid-cols-12 gap-5 items-start">
        <div className="lg:col-span-4">
          <div className="flex items-center justify-between gap-3">
            <p className="font-display text-xl text-white">{b.name}</p>
            <div className="flex items-center gap-3">
              {hasContract && (
                <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[color:var(--gold)]">
                  <FileSignature className="h-3 w-3" /> Contrato
                </span>
              )}
              <StatusBadge status={b.status} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-400">
            <a
              href={`tel:${b.phone}`}
              className="inline-flex items-center gap-1.5 hover:text-[color:var(--gold)]"
            >
              <Phone className="h-3.5 w-3.5" /> {b.phone}
            </a>
            <a
              href={`https://wa.me/${(b.phone || "").replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-[color:var(--whatsapp)]"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            {b.email && (
              <a
                href={`mailto:${b.email}`}
                className="inline-flex items-center gap-1.5 hover:text-[color:var(--teal)]"
              >
                <Mail className="h-3.5 w-3.5" /> {b.email}
              </a>
            )}
          </div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 mt-3">
            ID: {b.id.slice(0, 8)} · {created} · {(b.language || "pt").toUpperCase()}
          </p>
        </div>

        <div className="lg:col-span-5 grid sm:grid-cols-2 gap-4">
          <Info icon={MapPin} label="Origem" value={b.pickup} />
          <Info icon={MapPin} label="Destino" value={b.destination} />
          <Info icon={Calendar} label="Data / Hora" value={`${b.date} · ${b.time}`} />
          <Info icon={Users} label="Passageiros" value={String(b.passengers)} />
          {b.notes && (
            <div className="sm:col-span-2">
              <Info icon={MessageCircle} label="Notas" value={b.notes} />
            </div>
          )}
        </div>

        <div className="lg:col-span-3 flex flex-col gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1.5 flex items-center gap-1.5">
              <Euro className="h-3 w-3 text-[color:var(--gold)]" />
              Valor acordado (Faustitur)
            </p>
            <div className="flex items-center gap-2">
              <input
                data-testid={`value-input-${b.id}`}
                type="number"
                min={0}
                step="0.01"
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                placeholder="0,00"
                className="input-dark !py-2 text-sm flex-1"
              />
              <button
                data-testid={`value-save-${b.id}`}
                onClick={saveValue}
                title="Gravar contrato"
                className="inline-flex items-center justify-center bg-[color:var(--gold)] text-black px-3 py-2 hover:brightness-95 transition rounded-sm"
              >
                <Save className="h-4 w-4" />
              </button>
            </div>
            {hasContract && (
              <p className="mt-1.5 text-xs text-[color:var(--gold)]">
                €{Number(b.value).toFixed(2).replace(".", ",")} · contrato
              </p>
            )}
          </div>

          <select
            data-testid={`status-select-${b.id}`}
            value={b.status}
            onChange={(e) => onStatus(b.id, e.target.value)}
            className="input-dark !py-2 text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value} className="bg-black">
                {s.label}
              </option>
            ))}
          </select>

          <button
            data-testid={`delete-${b.id}`}
            onClick={() => onDelete(b.id)}
            className="inline-flex items-center justify-center gap-2 border border-red-500/30 text-red-300 hover:bg-red-500/10 px-3 py-2 text-xs tracking-wide transition-colors rounded-sm"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Eliminar
          </button>
        </div>
      </div>

      {/* Contract actions (visible in Contratos tab OR when value is set) */}
      {hasContract && (
        <div className="border-t border-white/5 px-5 sm:px-6 py-4 bg-black/20">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`toggle-details-${b.id}`}
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-[color:var(--gold)]"
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Dados do contratante & envio
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openPdf}
                data-testid={`pdf-${b.id}`}
                className="inline-flex items-center gap-2 border border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 px-3 py-2 text-xs tracking-wide rounded-sm transition"
              >
                <FileDown className="h-3.5 w-3.5" /> PDF
              </button>
              <button
                type="button"
                onClick={sendEmail}
                disabled={sending || !b.email}
                data-testid={`email-${b.id}`}
                className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black px-3 py-2 text-xs font-semibold tracking-wide rounded-sm hover:brightness-95 transition disabled:opacity-50"
                title={b.email ? `Enviar contrato para ${b.email}` : "Cliente sem email"}
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Email cliente
              </button>
              <button
                type="button"
                onClick={openWhatsApp}
                data-testid={`whatsapp-${b.id}`}
                className="inline-flex items-center gap-2 bg-[color:var(--whatsapp)] text-black px-3 py-2 text-xs font-semibold tracking-wide rounded-sm hover:brightness-95 transition"
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </button>
            </div>
          </div>

          {expanded && (
            <div
              data-testid={`extra-fields-${b.id}`}
              className="mt-4 grid sm:grid-cols-3 gap-3"
            >
              <label className="flex flex-col gap-1">
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                  Morada
                </span>
                <input
                  data-testid={`address-${b.id}`}
                  value={draftAddress}
                  onChange={(e) => setDraftAddress(e.target.value)}
                  placeholder="Rua, Cidade, Cód. Postal"
                  className="input-dark !py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                  País
                </span>
                <input
                  data-testid={`country-${b.id}`}
                  value={draftCountry}
                  onChange={(e) => setDraftCountry(e.target.value)}
                  placeholder="Portugal"
                  className="input-dark !py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                  NIF (opcional)
                </span>
                <input
                  data-testid={`taxid-${b.id}`}
                  value={draftTaxId}
                  onChange={(e) => setDraftTaxId(e.target.value)}
                  placeholder="000000000"
                  className="input-dark !py-2 text-sm"
                />
              </label>
              <div className="sm:col-span-3 flex justify-end">
                <button
                  type="button"
                  onClick={saveValue}
                  data-testid={`save-extra-${b.id}`}
                  className="inline-flex items-center gap-2 border border-white/10 text-zinc-200 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] px-3 py-2 text-xs tracking-wide rounded-sm transition"
                >
                  <Save className="h-3.5 w-3.5" /> Gravar dados do contrato
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {contractView && (
        <div className="border-t border-white/5 px-5 sm:px-6 py-3 text-[10px] tracking-[0.2em] uppercase text-zinc-600">
          Contrato · Art.º 18.º c) Lei 101/2023 · Art.º 10.º b) Reg. Transporte Táxi Portimão
        </div>
      )}
    </article>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-[color:var(--gold)]" />
      {label}
    </p>
    <p className="mt-1 text-sm text-white break-words">{value}</p>
  </div>
);

export default AdminDashboard;
